<section class="my-submissions-panel">
  <header class="my-submissions-panel__header">
    <div class="my-submissions-panel__title-group" *ngIf="showPanelHeader">
      <h2 class="my-submissions-panel__title">My Submissions</h2>
      <p class="my-submissions-panel__subtitle">View and manage your model evaluation submissions.</p>
    </div>

    <div class="my-submissions-panel__toolbar">
      <div class="my-submissions-dataset-select">
        <label class="my-submissions-dataset-select__label" for="my-submissions-dataset">Dataset</label>
        <select
          id="my-submissions-dataset"
          class="my-submissions-dataset-select__control"
          [disabled]="loading || datasetTabs.length === 0"
          [value]="activeDatasetId"
          (change)="onDatasetChange($any($event.target).value)"
        >
          <option *ngFor="let tab of datasetTabs" [value]="tab.id" [selected]="activeDatasetId === tab.id">
            {{ tab.label }}
          </option>
        </select>
      </div>
    </div>
  </header>

  <p *ngIf="loadError" class="my-submissions-panel__error" role="alert">
    {{ loadError }}
  </p>

  <div class="my-submissions-table-card">
    <ng-template #idCell let-row>
      <span class="my-submissions-id">#{{ row.id }}</span>
    </ng-template>

    <ng-template #jobIdCell let-row>
      <span class="my-submissions-job-id" [attr.title]="row.jobId">
        {{ truncateJobId(row.jobId) }}
      </span>
    </ng-template>

    <ng-template #statusCell let-row>
      <span class="my-submissions-status" [ngClass]="getStatusClass(row.status)">
        {{ getStatusLabel(row.status) }}
      </span>
    </ng-template>

    <ng-template #samplesCell let-row>
      <span class="my-submissions-numeric">{{ row.samples }}</span>
    </ng-template>

    <ng-template #timeCell let-row>
      <span class="my-submissions-numeric my-submissions-time">{{ formatTime(row.timeSeconds) }}</span>
    </ng-template>

    <ng-template #metricsCell let-row>
      <div class="my-submissions-metrics" *ngIf="hasMetrics(row.metrics); else noMetrics">
        <span
          *ngFor="let metric of getPreviewMetricEntries(row.metrics)"
          class="my-submissions-metrics__chip"
        >
          <span class="my-submissions-metrics__key">{{ formatMetricKey(metric.key) }}</span>
          <span class="my-submissions-metrics__value">{{ formatMetricValue(metric.value) }}</span>
        </span>
        <button
          *ngIf="hasMoreMetrics(row.metrics)"
          type="button"
          class="my-submissions-metrics__view-more"
          (click)="openMetricsModal(row); $event.stopPropagation()"
        >
          View more
        </button>
      </div>
      <ng-template #noMetrics>
        <span class="my-submissions-metrics__empty">-</span>
      </ng-template>
    </ng-template>

    <ng-template #submittedCell let-row>
      <span class="my-submissions-submitted">{{ row.submitted }}</span>
    </ng-template>

    <ng-template #actionCell let-row>
      <button
        type="button"
        class="my-submissions-action"
        [ngClass]="getActionClass(row.action)"
        [disabled]="row.action === 'selected'"
        (click)="onActionClick(row)"
      >
        {{ getActionLabel(row.action) }}
      </button>
    </ng-template>

    <app-table
      class="my-submissions-table"
      [columns]="tableColumns"
      [data]="submissions"
      [loading]="loading"
      [skeletonRows]="8"
      [scrollable]="true"
      scrollHeight="560px"
      [striped]="true"
      [hoverable]="true"
      [bordered]="true"
      emptyMessage="No submissions yet"
    ></app-table>
  </div>
</section>

<ng-template #leaderboardConfirmFooter>
  <div class="leaderboard-confirm-modal__footer">
    <button
      type="button"
      class="leaderboard-confirm-modal__btn leaderboard-confirm-modal__btn--cancel"
      [disabled]="selectingLeaderboard"
      (click)="closeLeaderboardConfirm()"
    >
      Cancel
    </button>
    <button
      type="button"
      class="leaderboard-confirm-modal__btn leaderboard-confirm-modal__btn--confirm"
      [disabled]="selectingLeaderboard"
      (click)="confirmAddToLeaderboard()"
    >
      <span *ngIf="!selectingLeaderboard">Add to leaderboard</span>
      <span *ngIf="selectingLeaderboard">Adding…</span>
    </button>
  </div>
</ng-template>

<app-modal
  [(visible)]="leaderboardConfirmVisible"
  header="Confirm leaderboard selection"
  [width]="'560px'"
  [modal]="true"
  [closable]="!selectingLeaderboard"
  [showConfirmButton]="false"
  [showCancelButton]="false"
  [footerTemplate]="leaderboardConfirmFooter"
  (onClose)="closeLeaderboardConfirm()"
>
  <div class="leaderboard-confirm-modal" *ngIf="pendingSubmission">
    <p class="leaderboard-confirm-modal__intro">
      Submission <strong>#{{ pendingSubmission.id }}</strong>
      <ng-container *ngIf="!currentLeaderboardSubmission">
        will be your first leaderboard entry for this dataset.
      </ng-container>
      <ng-container *ngIf="currentLeaderboardSubmission">
        will replace submission <strong>#{{ currentLeaderboardSubmission.id }}</strong> on the
        leaderboard for this dataset.
      </ng-container>
    </p>

    <div class="leaderboard-confirm-modal__table-wrap" *ngIf="leaderboardMetricRows.length > 0">
      <table class="leaderboard-confirm-modal__table">
        <thead>
          <tr>
            <th scope="col">Metric</th>
            <th scope="col">Current</th>
            <th scope="col">New</th>
            <th scope="col">Change</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let metric of leaderboardMetricRows">
            <th scope="row">{{ metric.label }}</th>
            <td>
              {{
                metric.current == null
                  ? '—'
                  : formatMetricValue(metric.current)
              }}
            </td>
            <td class="leaderboard-confirm-modal__new-value">
              {{ formatMetricValue(metric.next) }}
            </td>
            <td
              class="leaderboard-confirm-modal__change"
              [class.leaderboard-confirm-modal__change--new]="metric.changeLabel === 'new'"
            >
              {{ metric.changeLabel }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</app-modal>

<app-modal
  [(visible)]="viewLogsVisible"
  header="Submission logs"
  [width]="'720px'"
  [modal]="true"
  confirmLabel="Close"
  [showCancelButton]="false"
  (onConfirm)="closeViewLogs()"
  (onClose)="closeViewLogs()"
>
  <div class="submission-logs-modal" *ngIf="logSubmission">
    <p class="submission-logs-modal__meta">
      Submission <strong>#{{ logSubmission.id }}</strong>
      <span *ngIf="logSubmission.jobId"> · Job {{ logSubmission.jobId }}</span>
    </p>

    <div class="submission-logs-modal__editor" role="region" aria-label="Error log output">
      <div class="submission-logs-modal__editor-bar">
        <span class="submission-logs-modal__editor-filename">
          {{ logSubmission.errorTitle || 'evaluation-error.log' }}
        </span>
      </div>
      <pre class="submission-logs-modal__editor-content"><code>{{ getLogDisplayText(logSubmission) }}</code></pre>
    </div>
  </div>
</app-modal>

<app-modal
  [(visible)]="metricsModalVisible"
  header="Metrics"
  [width]="'480px'"
  [modal]="true"
  confirmLabel="Close"
  [showCancelButton]="false"
  (onConfirm)="closeMetricsModal()"
  (onClose)="closeMetricsModal()"
>
  <div class="my-submissions-metrics-modal" *ngIf="metricsModalSubmission">
    <p class="my-submissions-metrics-modal__meta">
      Submission <strong>#{{ metricsModalSubmission.id }}</strong>
      <span *ngIf="metricsModalSubmission.jobId"> · Job {{ metricsModalSubmission.jobId }}</span>
    </p>

    <div class="my-submissions-metrics-modal__grid">
      <div
        *ngFor="let metric of getOrderedMetricEntries(metricsModalSubmission.metrics)"
        class="my-submissions-metrics-modal__item"
      >
        <span class="my-submissions-metrics-modal__label">{{ formatMetricKey(metric.key) }}</span>
        <span class="my-submissions-metrics-modal__value">{{ formatMetricValue(metric.value) }}</span>
      </div>
    </div>
  </div>
</app-modal>
